import * as Sentry from "@sentry/vue";
import type { App } from "vue";
import { BuildMode } from "@/types";

export enum Event {
  ShareActionFallbacked = "Share action fallback",
}

// --- Browser / WebView / extension junk (never actionable) ---
// --- Stale-deploy / chunk-load noise ---
// --- Network / connectivity ---
const IGNORED_ERROR_PATTERNS: Array<string | RegExp> = [
  "Java object is gone", // Android in-app WebView
  "window.webkit.messageHandlers", // iOS WebView injection
  "Non-Error promise rejection captured with value", // browser extension / 3rd-party script
  "Importing a module script failed",
  "Unable to preload CSS",
  /Loading chunk \S+ failed/i,
  "ChunkLoadError",
  "Network Error",
  "Request failed with status 0",
];

// Drop anything originating in a browser extension.
const DENIED_URLS: Array<string | RegExp> = [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i, /^safari-web-extension:\/\//i];

export const initSentry = (app: App): void => {
  if (!isSentryAllowed()) {
    return;
  }

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: __APP_VERSION__,
    environment: import.meta.env.MODE,
    ignoreErrors: IGNORED_ERROR_PATTERNS,
    denyUrls: DENIED_URLS,
  });
};

export const trackEvent = (event: Event, severity: Sentry.SeverityLevel = "info"): void => {
  if (!isSentryAllowed()) {
    return;
  }

  Sentry.captureMessage(event, severity);
};

export const captureException = (error: unknown): void => {
  if (!isSentryAllowed()) {
    return;
  }

  Sentry.captureException(error);
};

const isSentryAllowed = (): boolean => {
  const allowedModes = [BuildMode.Production, BuildMode.Beta];
  const mode = import.meta.env.MODE as BuildMode;
  return allowedModes.includes(mode);
};
